import localeCurrency from 'locale-currency'

const ZERO_DECIMAL_CURRENCIES = [
    'BIF', // Burundian Franc
    'CLP', // Chilean Peso
    'DJF', // Djiboutian Franc
    'GNF', // Guinean Franc
    'ISK', // Icelandic Króna
    'JPY', // Japanese Yen
    'KMF', // Comorian Franc
    'KRW', // South Korean Won
    'MGA', // Malagasy Ariary
    'PYG', // Paraguayan Guarani
    'RWF', // Rwandan Franc
    'UGX', // Ugandan Shilling
    'UYW', // Uruguayan Nominal Wage Index Unit
    'VND', // Vietnamese Dong
    'VUV', // Vanuatu Vatu
    'XAF', // Central African CFA Franc
    'XOF', // West African CFA Franc
    'XPF', // CFP Franc
    'IDR', // Indonesian Rupiah
    'LAK', // Lao Kip
    'MMK', // Myanmar Kyat
    'OMR', // Omani Rial
    'RSD', // Serbian Dinar
    'TZS', // Tanzanian Shilling
]

export const capitaliseFirstLetter = (string) => {
    return string ? string.charAt(0).toUpperCase() + string.slice(1) : ''
}

export const isZeroDecimalCurrency = (currency = 'AUD') => {
    return ZERO_DECIMAL_CURRENCIES.includes(currency)
}

export const formatCurrency = (
    value,
    locale = 'en-AU',
    currency = 'AUD',
    minimumFractionDigits = 2,
    showCode = false,
) => {
    if (value == null || isNaN(value)) return '-'
    const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits,
    }).format(value)

    if (!showCode) return formatted

    return `${formatted} ${currency}`
}

export const formatCurrencyAutoLocale = ({ amount, currency = 'AUD', showCode = false }) => {
    currency = currency ? currency : 'AUD'
    const countryCode = localeCurrency.getLocales(currency)?.[0] || 'AU'

    let locale
    try {
        const normalizedLocale = new Intl.Locale('und', { region: countryCode }).maximize()
        locale = `${normalizedLocale.language}-${countryCode}`
    } catch {
        locale = `en-${countryCode}`
    }

    // Determine decimal places based on currency type
    const decimals = ZERO_DECIMAL_CURRENCIES.includes(currency) ? 0 : 2

    return formatCurrency(amount, locale, currency, decimals, showCode)
}

export const formatTimeZone = (offset = '') => {
    if (!offset) return ''

    // 1) Extract sign and strip it from the rest
    let sign = '+'
    let s = offset.trim()
    if (s.startsWith('-')) {
        sign = '-'
        s = s.slice(1)
    } else if (s.startsWith('+')) {
        s = s.slice(1)
    }

    // keep digits
    s = s.replace(/\D/g, '') // e.g. '10', '530', '1030'

    // exactly 4
    if (s.length <= 2) {
        s = s.padStart(2, '0') + '00'
    } else if (s.length === 3) {
        s = s.padStart(4, '0')
    } else {
        s = s.slice(0, 4)
    }

    return `${sign}${s.replace(/(\d{2})(\d{2})/, '$1:$2')}`
}

export function isEmpty(value) {
    if (value == null) return true

    if (typeof value === 'string') {
        return value.trim().length === 0
    }

    // array-likes: Array, arguments, NodeList, HTMLCollection, TypedArrays
    const isArrayLike =
        typeof value.length === 'number' &&
        typeof value !== 'function' &&
        (Array.isArray(value) ||
            Object.prototype.toString.call(value) === '[object Arguments]' ||
            value instanceof NodeList ||
            value instanceof HTMLCollection ||
            ArrayBuffer.isView(value))

    if (isArrayLike) {
        return value.length === 0
    }

    if (value instanceof Map || value instanceof Set) {
        return value.size === 0
    }

    if (typeof value === 'object') {
        return Object.keys(value).length === 0
    }

    return false
}

export const Capitalize = (str) => {
    if (!str || typeof str !== 'string') return ''
    return `${str.trim().charAt(0).toUpperCase()}${str.slice(1)}`
}

export const truncateMiddle = (text, maxLength = 15) => {
    if (text.length <= maxLength) return text

    const start = text.substring(0, maxLength / 2)
    const end = text.substring(text.length - maxLength / 2)

    return `${start}...${end}`
}

export const isMac = (navigator.userAgentData?.platform || navigator.userAgent).toLowerCase().includes('mac')

export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const formateMetaDateTime = (date) => {
    if (!date) return ''
    const d = new Date(date)
    return d.toLocaleString('en-AU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })
}

export const ellipses = (str, maxLength = 20, showEllipses = true) => {
    if (!str) return ''
    if (str.length <= maxLength) return str

    let subString = str.slice(0, maxLength - 3)

    if (showEllipses) subString = `${subString}...`

    return subString
}

/**
 * Converts camelCase strings to spaced words
 * @param {string} camelStr - The camelCase string to convert
 * @returns {string} The string with spaces between words
 * @example
 * camelCaseToSpaces('InitialCheckout') // 'Initial Checkout'
 * camelCaseToSpaces('AddToCart') // 'Add To Cart'
 * camelCaseToSpaces('PageView') // 'Page View'
 */
export const camelCaseToSpaces = (camelStr) => {
    if (!camelStr || typeof camelStr !== 'string') return ''

    return (
        camelStr
            // Insert space before uppercase letters that follow lowercase letters
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            // Insert space before uppercase letters that are followed by lowercase letters (for consecutive caps)
            .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
            .trim()
    )
}
